import { type CompanyTag, type Currency, DataQuality, type FundingRound, type Headcount } from "@prisma/client";
import { type AppContext } from "~/lib/context";
import { Measure } from "~/services/employee";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";
import { type WeightedJob } from "~/services/job";
import { computeMarketCompensationData } from "~/services/market-data/dataset-employee/compute-compensations/computeMarketCompensationData";
import { computeMarketGenderEqualityStats } from "~/services/market-data/dataset-employee/compute-compensations/computeMarketGenderEqualityStats";
import { computeMarketVariableCompensationStats } from "~/services/market-data/dataset-employee/compute-compensations/computeMarketVariableCompensationStats";
import { type MarketDataDatasetEmployee } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { type EmployeeLocationsForPredictiveBenchmark } from "~/services/market-data/getMarketDataPredictiveBenchmark";
import { MarketDataStatsSource, isEligibleToFallbackOnPredictiveData } from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

export type ComputeMarketCompensationStatsOptions = {
  currency: Currency;
  weightedJobs: WeightedJob[];
  level?: EmployeeLevel;
  locations: EmployeeLocationsForPredictiveBenchmark;
  percentiles: PercentileBounds;
  headcount?: Headcount | null;
  industry?: CompanyTag | null;
  industries?: CompanyTag[];
  fundingRounds?: FundingRound[];
};

export const computeMarketCompensationStats = async (
  ctx: AppContext,
  datasetEmployees: MarketDataDatasetEmployee[],
  options: ComputeMarketCompensationStatsOptions
) => {
  const compensationStats = await computeMarketCompensationData(ctx, datasetEmployees, {
    ...options,
    fallbackToPredictiveData: isEligibleToFallbackOnPredictiveData(ctx, { options }),
    measures: [Measure.BaseSalary, Measure.TotalCash, Measure.OnTargetEarnings],
  });

  if (!compensationStats.data) {
    return null;
  }

  if (compensationStats.data.measures.baseSalary.dataQuality === DataQuality.AI_ESTIMATED) {
    return {
      source: MarketDataStatsSource.PREDICTIVE,
      dataQuality: compensationStats.data.measures.totalCash.dataQuality,
      missingJobs: compensationStats.missingJobs,
      baseSalary: compensationStats.data.measures.baseSalary,
      totalCash: compensationStats.data.measures.totalCash,
      onTargetEarnings: compensationStats.data.measures.onTargetEarnings,
      companyEmployees: [],
    };
  }

  const onTargetBonus = await computeMarketVariableCompensationStats(ctx, datasetEmployees, options);
  const genderEquality = await computeMarketGenderEqualityStats(ctx, datasetEmployees, options);

  return {
    source: MarketDataStatsSource.DATASET,
    count: compensationStats.data.employeesCount,
    distinctCompaniesCount: compensationStats.data.distinctCompaniesCount,
    dataQuality: compensationStats.data.measures.totalCash.dataQuality,
    missingJobs: compensationStats.missingJobs,

    baseSalary: compensationStats.data.measures.baseSalary,
    totalCash: compensationStats.data.measures.totalCash,
    onTargetEarnings: compensationStats.data.measures.onTargetEarnings,
    onTargetBonus,
    genderEquality,
    companyEmployees: [],
  };
};
